import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from "@air/primitive-button";
import { animated, useSpring } from "@react-spring/web";
import pluralize from "pluralize";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { AutoSizer, List } from "react-virtualized";
const MAX_UPLOAD_HEIGHT = 450;
const HEADER_ROW_HEIGHT = 30;
const ERROR_HEADER_ROW_HEIGHT = 42;
const HEADER_ROW_TOP_MARGIN = 6;
const UPLOAD_ROW_HEIGHT = 60;
const AbortedItemsHeader = /*#__PURE__*/ memo((param)=>{
    let { numberOfItems, onRetryAll, style } = param;
    return /*#__PURE__*/ _jsxs("div", {
        className: "mb-1.5 ml-2 mr-4 flex items-center justify-between border-b border-b-grey-5 py-2 pl-2 pr-0",
        style: style,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "text-10 font-bold uppercase tracking-wider text-grey-9",
                children: pluralize("Cancelled", numberOfItems, true)
            }),
            onRetryAll && /*#__PURE__*/ _jsx(Button, {
                appearance: "ghost",
                color: "grey",
                onClick: onRetryAll,
                size: "small",
                children: "Retry all"
            })
        ]
    });
});
AbortedItemsHeader.displayName = "AbortedItemsHeader";
const ErroredItemsHeader = /*#__PURE__*/ memo((param)=>{
    let { numberOfItems, onRetryAll, style } = param;
    return /*#__PURE__*/ _jsxs("div", {
        className: "mb-1.5 ml-2 mr-4 flex items-center justify-between border-b border-b-grey-5 py-2 pl-2 pr-0",
        style: style,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "text-10 font-bold uppercase tracking-wider text-grey-9",
                children: pluralize("Errors", numberOfItems, true)
            }),
            onRetryAll && /*#__PURE__*/ _jsx(Button, {
                appearance: "ghost",
                color: "grey",
                onClick: onRetryAll,
                size: "small",
                children: "Retry all"
            })
        ]
    });
});
ErroredItemsHeader.displayName = "ErroredItemsHeader";
const CompletedItemsHeader = /*#__PURE__*/ memo((param)=>{
    let { numberOfItems, style } = param;
    return /*#__PURE__*/ _jsxs("div", {
        className: "mb-0.5 flex h-7 items-center bg-grey-3 px-4 text-10 font-bold uppercase tracking-wider text-grey-9",
        style: style,
        children: [
            numberOfItems,
            " Completed"
        ]
    });
});
CompletedItemsHeader.displayName = "CompletedItemsHeader";
var PaneListRowType;
(function(PaneListRowType) {
    PaneListRowType[PaneListRowType["item"] = 0] = "item";
    PaneListRowType[PaneListRowType["header"] = 1] = "header";
    PaneListRowType[PaneListRowType["errorHeader"] = 2] = "errorHeader";
})(PaneListRowType || (PaneListRowType = {}));
export const FileStatusTrackingPanelList = /*#__PURE__*/ memo((param)=>{
    let { isUploaderOpen, ongoingItems, erroredItems, completedItems, abortedItems, stoppedItems, onRetryAll } = param;
    const listRef = useRef(null);
    const rows = useMemo(()=>{
        const itemsToRender = [];
        if (stoppedItems.length) {
            itemsToRender.push(...stoppedItems.map((item)=>({
                    type: 0,
                    row: item
                })));
        }
        if (ongoingItems.length) {
            itemsToRender.push(...ongoingItems.map((item)=>({
                    type: 0,
                    row: item
                })));
        }
        if (erroredItems.length) {
            itemsToRender.push({
                type: 2,
                row: /*#__PURE__*/ _jsx(ErroredItemsHeader, {
                    numberOfItems: erroredItems.length,
                    onRetryAll: onRetryAll,
                    style: {
                        marginTop: itemsToRender.length > 0 ? 6 : 0
                    }
                })
            });
            itemsToRender.push(...erroredItems.map((item)=>({
                    type: 0,
                    row: item
                })));
        }
        if (completedItems.length) {
            itemsToRender.push({
                type: 1,
                row: /*#__PURE__*/ _jsx(CompletedItemsHeader, {
                    numberOfItems: completedItems.length,
                    style: {
                        marginTop: itemsToRender.length > 0 ? 6 : 0
                    }
                })
            });
            itemsToRender.push(...completedItems.map((item)=>({
                    type: 0,
                    row: item
                })));
        }
        if (abortedItems.length) {
            itemsToRender.push({
                type: 2,
                row: /*#__PURE__*/ _jsx(AbortedItemsHeader, {
                    numberOfItems: abortedItems.length,
                    onRetryAll: onRetryAll,
                    style: {
                        marginTop: itemsToRender.length > 0 ? 6 : 0
                    }
                })
            });
            itemsToRender.push(...abortedItems.map((item)=>({
                    type: 0,
                    row: item
                })));
        }
        return itemsToRender;
    }, [
        completedItems,
        erroredItems,
        onRetryAll,
        ongoingItems,
        abortedItems,
        stoppedItems
    ]);
    const rowRenderer = useCallback((param)=>{
        let { index, style, key } = param;
        return /*#__PURE__*/ _jsx("li", {
            style: style,
            children: rows[index].row
        }, key);
    }, [
        rows
    ]);
    const getRowHeight = useCallback((param)=>{
        let { index } = param;
        if (rows[index].type === 1) {
            return index === 0 ? HEADER_ROW_HEIGHT : HEADER_ROW_HEIGHT + HEADER_ROW_TOP_MARGIN;
        } else if (rows[index].type === 2) {
            return index === 0 ? ERROR_HEADER_ROW_HEIGHT : ERROR_HEADER_ROW_HEIGHT + HEADER_ROW_TOP_MARGIN;
        } else {
            return UPLOAD_ROW_HEIGHT;
        }
    }, [
        rows
    ]);
    const listHeight = useMemo(()=>{
        const allRowsHeight = rows.reduce((acc, _, index)=>{
            acc += getRowHeight({
                index
            });
            return acc;
        }, 0);
        return Math.min(MAX_UPLOAD_HEIGHT, allRowsHeight);
    }, [
        getRowHeight,
        rows
    ]);
    const [springs] = useSpring(()=>({
            height: isUploaderOpen ? listHeight : 0,
            opacity: isUploaderOpen ? 1 : 0
        }), [
        isUploaderOpen,
        listHeight
    ]);
    useEffect(()=>{
        var _listRef_current;
        listRef === null || listRef === void 0 ? void 0 : (_listRef_current = listRef.current) === null || _listRef_current === void 0 ? void 0 : _listRef_current.recomputeRowHeights();
    }, [
        ongoingItems.length,
        erroredItems.length,
        completedItems.length
    ]);
    return /*#__PURE__*/ _jsx(animated.div, {
        className: "m-0 list-none overflow-y-auto overflow-x-hidden border-t border-grey-5",
        style: {
            height: springs.height,
            opacity: springs.opacity,
            maxHeight: MAX_UPLOAD_HEIGHT
        },
        children: isUploaderOpen ? /*#__PURE__*/ _jsx(AutoSizer, {
            disableHeight: true,
            children: (param)=>{
                let { width } = param;
                return /*#__PURE__*/ _jsx(List, {
                    ref: listRef,
                    rowRenderer: rowRenderer,
                    rowCount: rows.length,
                    height: listHeight,
                    width: width,
                    rowHeight: getRowHeight
                });
            }
        }) : null
    });
});
FileStatusTrackingPanelList.displayName = "FileStatusTrackingPanelList";
